import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { UserRole } from '@/lib/auth';

// Layout components
import MainLayout from '@/components/layouts/MainLayout';

// Page components
import LoginPage from '@/pages/auth/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import CustomersPage from '@/pages/customers/CustomersPage';
import CustomerDetailPage from '@/pages/customers/CustomerDetailPage';
import ResellersPage from '@/pages/resellers/ResellersPage';
import ResellerDetailPage from '@/pages/resellers/ResellerDetailPage';
import SubscriptionsPage from '@/pages/subscriptions/SubscriptionsPage';
import SubscriptionDetailPage from '@/pages/subscriptions/SubscriptionDetailPage';
import LicensesPage from '@/pages/licenses/LicensesPage';
import LicenseDetailPage from '@/pages/licenses/LicenseDetailPage';
import ProductsPage from '@/pages/products/ProductsPage';
import ProductDetailPage from '@/pages/products/ProductDetailPage';
import NotFoundPage from '@/pages/NotFoundPage';

// Auth guard for routes
interface ProtectedRouteProps {
  allowedRoles: UserRole[];
  children: JSX.Element;
}

function ProtectedRoute({ allowedRoles, children }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}

export default function App() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!isLoading && !user && location.pathname !== '/login') {
      navigate('/login');
    }
    
    // Redirect from login to dashboard if already authenticated
    if (!isLoading && user && location.pathname === '/login') {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate, location.pathname]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      
      <Route path="/" element={
        <ProtectedRoute allowedRoles={['distributor', 'reseller', 'customer']}>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        
        {/* Customers routes */}
        <Route path="customers" element={
          <ProtectedRoute allowedRoles={['distributor', 'reseller']}>
            <CustomersPage />
          </ProtectedRoute>
        } />
        <Route path="customers/:id" element={
          <ProtectedRoute allowedRoles={['distributor', 'reseller']}>
            <CustomerDetailPage />
          </ProtectedRoute>
        } />
        
        {/* Resellers routes - only for distributor */}
        <Route path="resellers" element={
          <ProtectedRoute allowedRoles={['distributor']}>
            <ResellersPage />
          </ProtectedRoute>
        } />
        <Route path="resellers/:id" element={
          <ProtectedRoute allowedRoles={['distributor']}>
            <ResellerDetailPage />
          </ProtectedRoute>
        } />
        
        {/* Subscriptions routes */}
        <Route path="subscriptions" element={
          <ProtectedRoute allowedRoles={['distributor', 'reseller', 'customer']}>
            <SubscriptionsPage />
          </ProtectedRoute>
        } />
        <Route path="subscriptions/:id" element={
          <ProtectedRoute allowedRoles={['distributor', 'reseller', 'customer']}>
            <SubscriptionDetailPage />
          </ProtectedRoute>
        } />
        
        {/* Licenses routes */}
        <Route path="licenses" element={
          <ProtectedRoute allowedRoles={['distributor', 'reseller', 'customer']}>
            <LicensesPage />
          </ProtectedRoute>
        } />
        <Route path="licenses/:id" element={
          <ProtectedRoute allowedRoles={['distributor', 'reseller', 'customer']}>
            <LicenseDetailPage />
          </ProtectedRoute>
        } />
        
        {/* Products routes */}
        <Route path="products" element={
          <ProtectedRoute allowedRoles={['distributor', 'reseller']}>
            <ProductsPage />
          </ProtectedRoute>
        } />
        <Route path="products/:id" element={
          <ProtectedRoute allowedRoles={['distributor', 'reseller']}>
            <ProductDetailPage />
          </ProtectedRoute>
        } />
        
        {/* Not found route */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}